/**
 * PDF Font Management Utility
 *
 * Provides centralized font loading and language detection for PDF generation.
 * Supports Korean (and other CJK languages) through Unicode font embedding.
 *
 * When content contains CJK characters, loads Noto Sans KR fonts.
 * For Latin-only content, uses standard PDF fonts (smaller file size).
 */

import { PDFDocument, StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

// ============================================================================
// Types
// ============================================================================

// Type for embedded PDF fonts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EmbeddedFont = any;

export interface FontFamily {
  regular: EmbeddedFont;
  bold: EmbeddedFont;
  italic: EmbeddedFont;
  boldItalic?: EmbeddedFont;
}

// Re-export the type for use in other modules
export type PDFFont = EmbeddedFont;

export type LanguageHint = 'korean' | 'chinese' | 'japanese' | 'latin' | 'auto';

export interface FontLoadOptions {
  languageHint?: LanguageHint;
  /** Force Unicode font even for Latin content (useful for mixed-language docs) */
  forceUnicodeFont?: boolean;
}

// ============================================================================
// Language Detection
// ============================================================================

// Unicode ranges for CJK detection
const CJK_RANGES = {
  // Korean Hangul syllables, Jamo, compatibility Jamo, extended
  korean: /[\uac00-\ud7af\u1100-\u11ff\u3130-\u318f\ua960-\ua97f\ud7b0-\ud7ff]/,
  // CJK Unified Ideographs (shared by Chinese/Japanese/Korean)
  chinese: /[\u4e00-\u9fff\u3400-\u4dbf]/,
  // Japanese Hiragana, Katakana, Katakana extensions
  japanese: /[\u3040-\u309f\u30a0-\u30ff\u31f0-\u31ff]/,
};

/**
 * Detect the primary language of text based on character ranges.
 * Returns the first CJK language detected, or 'latin' if none found.
 */
export function detectLanguage(text: string): LanguageHint {
  if (!text) return 'latin';

  // Check Korean first (most specific - Hangul syllables)
  if (CJK_RANGES.korean.test(text)) return 'korean';
  // Japanese kana is unique to Japanese
  if (CJK_RANGES.japanese.test(text)) return 'japanese';
  // Chinese characters (could also be used in Japanese/Korean, check last)
  if (CJK_RANGES.chinese.test(text)) return 'chinese';

  return 'latin';
}

/**
 * Check if text contains any characters that require Unicode font support.
 * This includes all characters outside the Latin-1 range (> 0xFF) that
 * cannot be represented by a simple ASCII replacement.
 */
export function requiresUnicodeFont(text: string): boolean {
  if (!text) return false;

  // Common symbol replacements that can be handled by standard fonts
  const replaceable = new Set([
    '\u2192', '\u2013', '\u2014', '\u2018', '\u2019', '\u201C', '\u201D',
    '\u00A9', '\u00A7', '\u2022', '\u2026', '\u2032', '\u2033', '\u2010',
    '\u2011', '\u2012', '\u2015', '\u00AB', '\u00BB', '\u2039', '\u203A',
    '\u00B0', '\u00B1', '\u00D7', '\u00F7', '\u2264', '\u2265', '\u2260',
    '\u2248', '\u221E', '\u2713', '\u2714', '\u2715', '\u2716', '\u2717',
    '\u2718', '\u25CF', '\u25CB', '\u25A0', '\u25A1', '\u25B6', '\u25C0',
    '\u2605', '\u2606',
  ]);

  return Array.from(text).some(ch => {
    const code = ch.charCodeAt(0);
    // Characters within Latin-1 extended (0-255) are fine
    if (code <= 0xff) return false;
    // Check if it's a replaceable symbol
    if (replaceable.has(ch)) return false;
    // Any other character > 0xFF requires Unicode font
    return true;
  });
}

// ============================================================================
// Font Loading
// ============================================================================

// Font cache to avoid re-fetching
const fontCache = new Map<string, ArrayBuffer>();

/**
 * Fetch font file bytes from public directory.
 * Caches results to avoid redundant network requests.
 */
async function fetchFontBytes(fontPath: string): Promise<ArrayBuffer> {
  if (fontCache.has(fontPath)) {
    return fontCache.get(fontPath)!;
  }

  const response = await fetch(fontPath);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${fontPath} (${response.status})`);
  }

  const bytes = await response.arrayBuffer();
  fontCache.set(fontPath, bytes);
  return bytes;
}

/**
 * Get font file paths for a specific language.
 * Falls back to Korean fonts which have good CJK coverage.
 */
function getFontPathsForLanguage(lang: LanguageHint): { regular: string; bold: string } {
  // Currently using Noto Sans KR for all CJK - it has good coverage
  // Can be extended to use language-specific fonts if needed
  switch (lang) {
    case 'korean':
    case 'chinese':
    case 'japanese':
    default:
      return {
        regular: '/fonts/NotoSansKR-Regular.ttf',
        bold: '/fonts/NotoSansKR-Bold.ttf',
      };
  }
}

/**
 * Load a Unicode font family (Noto Sans KR) for CJK content.
 * Requires fontkit to be registered with the PDF document.
 */
export async function loadUnicodeFontFamily(pdfDoc: InstanceType<typeof PDFDocument>, languageHint: LanguageHint = 'korean'): Promise<FontFamily> {
  // Register fontkit for custom font embedding
  pdfDoc.registerFontkit(fontkit);

  const fontPaths = getFontPathsForLanguage(languageHint);

  // Load fonts in parallel
  const [regularBytes, boldBytes] = await Promise.all([
    fetchFontBytes(fontPaths.regular),
    fetchFontBytes(fontPaths.bold),
  ]);

  // Embed fonts with subsetting enabled (only includes used characters)
  const regular = await pdfDoc.embedFont(regularBytes, { subset: true });
  const bold = await pdfDoc.embedFont(boldBytes, { subset: true });

  // CJK fonts typically don't have italic variants - use regular as fallback
  const italic = regular;

  return { regular, bold, italic };
}

/**
 * Load standard PDF fonts (Helvetica or Times Roman).
 * Smaller file size, suitable for Latin-only content.
 */
export async function loadStandardFonts(
  pdfDoc: InstanceType<typeof PDFDocument>,
  family: 'helvetica' | 'times' = 'helvetica'
): Promise<FontFamily> {
  if (family === 'times') {
    return {
      regular: await pdfDoc.embedFont(StandardFonts.TimesRoman),
      bold: await pdfDoc.embedFont(StandardFonts.TimesRomanBold),
      italic: await pdfDoc.embedFont(StandardFonts.TimesRomanItalic),
    };
  }

  return {
    regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
    bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
    italic: await pdfDoc.embedFont(StandardFonts.HelveticaOblique),
    boldItalic: await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique),
  };
}

/**
 * Load appropriate font family based on content language.
 * Automatically detects CJK content and loads Unicode fonts when needed.
 */
export async function loadFontFamily(
  pdfDoc: InstanceType<typeof PDFDocument>,
  options: FontLoadOptions & { standardFamily?: 'helvetica' | 'times' } = {}
): Promise<FontFamily> {
  const { languageHint = 'auto', forceUnicodeFont = false, standardFamily = 'helvetica' } = options;

  // If forcing Unicode font or language hint suggests CJK, use Unicode fonts
  if (forceUnicodeFont || (languageHint !== 'latin' && languageHint !== 'auto')) {
    return loadUnicodeFontFamily(pdfDoc, languageHint === 'auto' ? 'korean' : languageHint);
  }

  // Default to standard fonts for Latin content
  return loadStandardFonts(pdfDoc, standardFamily);
}
